import { z } from 'zod';

import { baseDtoSchema, apiResponseSchema } from './common';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const UserRole = {
  Admin: 'Admin',
  User: 'User',
} as const;

// ---------------------------------------------------------------------------
// Request DTOs
// ---------------------------------------------------------------------------

export const loginRequestSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export type LoginRequestDto = z.infer<typeof loginRequestSchema>;

export const registerRequestSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  verificationCode: z.string().min(1, 'Verification code is required'),
});

export type UserRegisterRequestDto = z.infer<typeof registerRequestSchema>;

export const googleLoginRequestSchema = z.object({
  credential: z.string(),
});

export type GoogleLoginRequestDto = z.infer<typeof googleLoginRequestSchema>;

export const forgotPasswordRequestSchema = z.object({
  email: z.string().email('Invalid email address'),
});

export type ForgotPasswordRequestDto = z.infer<typeof forgotPasswordRequestSchema>;

export const resetPasswordRequestSchema = z.object({
  email: z.string().email(),
  token: z.string(),
  newPassword: z.string().min(6, 'Password must be at least 6 characters'),
});

export type ResetPasswordRequestDto = z.infer<typeof resetPasswordRequestSchema>;

export const sendVerificationCodeRequestSchema = z.object({
  email: z.string().email('Invalid email address'),
});

export type SendVerificationCodeRequestDto = z.infer<typeof sendVerificationCodeRequestSchema>;

// ---------------------------------------------------------------------------
// Response DTOs
// ---------------------------------------------------------------------------

export const userApiDtoSchema = baseDtoSchema.extend({
  email: z.string(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  userName: z.string().nullable().optional(),
  profilePictureUrl: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  websiteUrl: z.string().nullable().optional(),
  linkedinUrl: z.string().nullable().optional(),
  githubUrl: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),
  isPro: z.boolean(),
  proEndDateUtc: z.string().nullable().optional(),
  isProActive: z.boolean(),
  freeTrialCount: z.number(),
});

export type UserApiDto = z.infer<typeof userApiDtoSchema>;

export const loginResponseSchema = apiResponseSchema.extend({
  token: z.string(),
  user: userApiDtoSchema.nullable().optional(),
  roles: z.array(z.string()),
});

export type LoginResponseDto = z.infer<typeof loginResponseSchema>;

export const refreshTokenResponseSchema = apiResponseSchema.extend({
  token: z.string(),
  expiresAt: z.string(),
});

export type RefreshTokenResponseDto = z.infer<typeof refreshTokenResponseSchema>;

// ---------------------------------------------------------------------------
// Legacy compatibility aliases (from src/types/auth.ts)
// ---------------------------------------------------------------------------

/** @deprecated Use UserApiDto instead */
export type User = UserApiDto;

/** @deprecated Use LoginResponseDto instead */
export type AuthResponse = LoginResponseDto & { data?: LoginResponseDto['user'] };

/** @deprecated Use RefreshTokenResponseDto instead */
export type RefreshTokenResponse = RefreshTokenResponseDto;

/** @deprecated Use LoginResponseDto instead */
export type AuthUserData = UserApiDto;
